import { useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Status, STATUS_LABELS, Priority, DISPOSITION_LABELS, DISPOSITION_ICONS } from '@launchpad/shared';

const STALE_THRESHOLD_DAYS = 14;
const LONG_PRESS_MS = 500;

export function ApplicationCard({ app, onClick, onContextMenu }) {
  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.appId, data: { status: app.status } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityColor = {
    [Priority.HIGH]: 'border-l-red-400',
    [Priority.MEDIUM]: 'border-l-amber-400',
    [Priority.LOW]: 'border-l-green-400',
  }[app.priority] || 'border-l-transparent';

  const activity = getActivityText(app);
  const stale = getStaleText(app);
  const isRejected = app.status === Status.REJECTED;

  const bgColor = !isRejected && activity?.urgent ? 'bg-red-50' : !isRejected && stale ? 'bg-amber-50' : 'bg-white';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => { if (!didLongPress.current) onClick?.(app); }}
      onContextMenu={(e) => {
        if (onContextMenu) {
          e.preventDefault();
          onContextMenu(app, { x: e.clientX, y: e.clientY });
        }
      }}
      onTouchStart={(e) => {
        didLongPress.current = false;
        const touch = e.touches[0];
        longPressTimer.current = setTimeout(() => {
          didLongPress.current = true;
          onContextMenu?.(app, { x: touch.clientX, y: touch.clientY });
        }, LONG_PRESS_MS);
      }}
      onTouchEnd={() => clearTimeout(longPressTimer.current)}
      onTouchMove={() => clearTimeout(longPressTimer.current)}
      role="button"
      aria-label={`${app.company}, ${app.role}`}
      className={`group relative cursor-grab rounded border border-slate-200 border-l-4 ${priorityColor} ${bgColor} p-3 shadow-sm hover:shadow-md active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-primary-500`}
    >
      <p className="text-sm font-medium text-slate-900 truncate">
        {app.boardType === 'networking' && <span title="Networking">🤝 </span>}
        {app.company}
      </p>
      <p className="text-xs text-slate-600 truncate">{app.role}</p>
      {activity && !isRejected && (
        <p className={`mt-1.5 text-[10px] ${activity.urgent ? 'text-accent-600 font-medium' : 'text-slate-400'}`}>
          {activity.text}
        </p>
      )}
      {app.lastContacted && (
        <p className="mt-1 text-[10px] text-slate-400">
          ✉ Contacted {relativeTime(app.lastContacted, Date.now())}
        </p>
      )}
      {stale && (
        <p className="mt-1 text-[10px] text-amber-600 font-medium">
          {stale}
        </p>
      )}
      {app.boardType !== 'networking' && app.disposition && DISPOSITION_LABELS[app.disposition] && (
        <p className="mt-1 text-[10px] text-slate-400 truncate" title={app.dispositionNote || DISPOSITION_LABELS[app.disposition]}>
          {DISPOSITION_ICONS[app.disposition]} {DISPOSITION_LABELS[app.disposition]}
        </p>
      )}
      {app.tags?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {app.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {tag}
            </span>
          ))}
          {app.tags.length > 3 && (
            <span className="text-[10px] text-slate-400">+{app.tags.length - 3}</span>
          )}
        </div>
      )}
      {app.notes && (
        <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-56 rounded border border-slate-200 bg-slate-800 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          {app.notes.split('\n').slice(0, 2).join('\n')}
        </div>
      )}
    </div>
  );
}

function getStaleText(app) {
  if (app.status !== Status.APPLIED && app.status !== Status.INTERVIEWING) return null;

  // Don't show stale if there's an upcoming next action
  if (app.nextAction && app.nextActionDate) {
    const daysUntil = Math.ceil((new Date(app.nextActionDate) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
    if (daysUntil >= 0) return null;
  }

  const lastActivity = getLastActivityDate(app);
  if (!lastActivity) return null;

  const days = Math.floor((Date.now() - lastActivity) / 86400000);
  if (days < STALE_THRESHOLD_DAYS) return null;

  const weeks = Math.floor(days / 7);
  return `⏸ No update in ${weeks >= 2 ? `${weeks} weeks` : `${days} days`}`;
}

function getLastActivityDate(app) {
  // Use the timestamp of the most recent status change into the current status
  if (app.statusHistory?.length > 0) {
    return new Date(app.statusHistory[app.statusHistory.length - 1].timestamp).getTime();
  }
  // Fallback: dateApplied (entered Applied without status history)
  if (app.dateApplied) return new Date(app.dateApplied).getTime();
  return null;
}

function getActivityText(app) {
  const now = Date.now();
  const today = new Date().toISOString().slice(0, 10);

  // Priority 1: Next action with upcoming/overdue date
  if (app.nextAction && app.nextActionDate) {
    const daysUntil = Math.ceil((new Date(app.nextActionDate) - new Date(today)) / 86400000);
    if (daysUntil < 0) return { text: `⚠ ${app.nextAction} (overdue)`, urgent: true };
    if (daysUntil === 0) return { text: `→ ${app.nextAction} (today)`, urgent: false };
    if (daysUntil <= 3) return { text: `→ ${app.nextAction} (${formatFutureDate(app.nextActionDate)})`, urgent: false };
    return { text: `→ ${app.nextAction}`, urgent: false };
  }

  // Priority 2: Next action without date
  if (app.nextAction) {
    return { text: `→ ${app.nextAction}`, urgent: false };
  }

  // Priority 3: Last status change
  if (app.statusHistory?.length > 0) {
    const last = app.statusHistory[app.statusHistory.length - 1];
    return { text: `${STATUS_LABELS[last.to] || last.to} ${relativeTime(last.timestamp, now)}`, urgent: false };
  }

  // Priority 4: Updated timestamp
  if (app.updatedAt) {
    return { text: `Updated ${relativeTime(app.updatedAt, now)}`, urgent: false };
  }

  return null;
}

function relativeTime(isoString, now) {
  const diff = now - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return 'just now';
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatFutureDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.toLocaleDateString(undefined, { weekday: 'short' });
  return day;
}
